import { type FC } from "react";
import { Loader2Icon, CheckCircle2Icon, FileTextIcon, XCircleIcon } from "lucide-react";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";

const GetUnstructuredContextToolCallComponent: FC<{
  toolName: string;
  argsText: string;
  result?: unknown;
  status?: { type: "running" | "complete" | "incomplete"; reason?: string };
  args?: any;
  toolCallId?: string;
  [key: string]: any;
}> = ({ argsText, result, status }) => {
  const isRunning = status?.type === "running" || (status === undefined && result === undefined);
  const isError = status?.type === "incomplete" && status?.reason === "error";
  const isComplete = status?.type === "complete" && !isError;

  // Parse result to get document details
  let title = "";
  let errorMessage = "";
  let bodyLength = 0;
  if (result !== undefined) {
    try {
      const parsed = typeof result === "string" ? JSON.parse(result) : result;
      title = parsed?.title || "";
      bodyLength = (parsed?.body || "").length;
      errorMessage = parsed?.error || "";
    } catch {
      // ignore parse errors
    }
  }

  const hasError = isError || !!errorMessage;

  return (
    <Card>
      <div className={cn(
        "p-3",
        isRunning ? "bg-primary/10" : hasError ? "bg-destructive/10" : "bg-muted/50"
      )}>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">Retrieving document...</p>
            </div>
          ) : hasError ? (
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive font-medium">
                {errorMessage || "Failed to retrieve document"}
              </p>
            </div>
          ) : isComplete ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <FileTextIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <p className="text-sm m-0 text-muted-foreground">
                  Retrieving document
                </p>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
                <p className="text-sm m-0 text-foreground">
                  {title ? (
                    <>Loaded <strong>{title}</strong>{bodyLength > 0 && <span className="text-muted-foreground"> ({(bodyLength / 1000).toFixed(1)}k chars)</span>}</>
                  ) : (
                    <>Document loaded</>
                  )}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">Retrieving document...</p>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export const GetUnstructuredContextToolCall = GetUnstructuredContextToolCallComponent as React.ComponentType<any>;
